import { useContext, useEffect, useState } from "react";
import useDocumentTitle from "../../utils/useDocumentTitle";

import { useHistory } from "react-router";

import AuthApi from "../../context/AuthApi";

import dayjs from "dayjs";
import utc from "dayjs-plugin-utc";

import { WarningLettersTable } from "./WarningLettersTable";

import styles from "./WarningLetters.module.scss";

import PageContent from "../../components/PageContent";
import Search from "../../components/Search";
import Section from "../../components/Section";
import SectionTitle from "../../components/SectionTitle";
import { DashboardLayout } from "../../layouts/DashboardLayout";
import { getLocalValue } from "../../utils/auth";
import { getQueryVariable } from "../../utils/getQueryParam";

import { Button } from "@material-ui/core";
import TextField from "@material-ui/core/TextField";
import { FilterListOutlined } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import Autocomplete from "../../components/Autocomplete";
import DateRangePicker from "../../components/DateRangePicker";
import { useNotification } from "../../context/notification";
import { apiRequest } from "../../utils/api";

dayjs.extend(utc);

export function WarningLetters(props) {
  useDocumentTitle(props.title);
  const { t } = useTranslation();
  const history = useHistory();
  const Auth = useContext(AuthApi);
  const { addNotification } = useNotification();
  const [id, setId] = useState(getQueryVariable("id"));
  const uid = getLocalValue("uid");
  const userRole = getLocalValue("u_coaching_role_db");
  const [searchKey, setSearchKey] = useState("");

  const [subjectList, setSubjectList] = useState([]);
  const [coacheeNames, setCoacheeNames] = useState([]);
  const [campaignList, setCampaignList] = useState([]);

  const [showFilter, setShowFilter] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState({});
  const [selectedSubject, setSelectedSubject] = useState();
  const [selectedNames, setSelectedNames] = useState();
  const [selectedCampaign, setSelectedCampaign] = useState();
  const [filterParam, setFilterParam] = useState("");
  const handleDateFilter = val => {
    setSelectedDateRange(val);
  };

  useEffect(() => {
    if (showFilter) {
      let param = `&start_date=${dayjs(selectedDateRange.startDate).format(
        "YYYY-MM-DD"
      )}`;
      param += `&end_date=${dayjs(selectedDateRange.endDate).format(
        "YYYY-MM-DD"
      )}`;
      if (selectedSubject) {
        param += `&subject=${selectedSubject.subject}&sub_subject=${selectedSubject.sub_subject}`;
      }
      if (selectedNames) {
        param += `&name=${selectedNames.uid}`;
      }
      if (selectedCampaign) {
        param += `&campaign=${selectedCampaign.campaign_id}`;
      }

      setFilterParam(param);
    } else {
      setFilterParam("");
    }
  }, [selectedSubject, selectedNames, selectedCampaign, selectedDateRange]);

  useEffect(() => {
    if (!showFilter) {
      setFilterParam("");
    }
  }, [showFilter]);

  useEffect(() => {
    getSubjectList();
    getCoacheeNames();
    getCampaignList();
  }, []);

  async function getSubjectList() {
    const response = await apiRequest.get(`api/getHRViewSubjects?uid=${uid}`);
    if (response.status === 200) {
      const data = response.data.data;
      setSubjectList(data);
    } else {
      addNotification(
        t("error.couldNotGetSession") + ` (Status code: ${response.status})`,
        "error"
      );
    }
  }

  async function getCoacheeNames() {
    const response = await apiRequest.get(`api/getHRViewNames?uid=${uid}`);
    if (response.status === 200) {
      const data = response.data.data;
      setCoacheeNames(data);
    } else {
      addNotification(
        t("error.couldNotGetSession") + ` (Status code: ${response.status})`,
        "error"
      );
    }
  }
  async function getCampaignList() {
    const response = await apiRequest.get(`api/getHRViewCampaigns?uid=${uid}`);
    if (response.status === 200) {
      const data = response.data.data;
      setCampaignList(data);
    } else {
      addNotification(
        t("error.couldNotGetSession") + ` (Status code: ${response.status})`,
        "error"
      );
    }
  }

  return (
    <DashboardLayout>
      <div className={styles.pageWrapper}>
        <PageContent>
          <Section>
            <SectionTitle>Warning Letters</SectionTitle>

            <div className={styles.filterWrapper}>
              <div className={styles.search}>
                <Search
                  handleSearch={val => {
                    setSearchKey(val);
                  }}
                />
              </div>
              <div
                onClick={() => {
                  setShowFilter(!showFilter);
                }}
                className={styles.filterButton}
              >
                Filter
                <FilterListOutlined />
              </div>
              {showFilter && (
                <div className={styles.filterRow}>
                  <div className={styles.dateRange}>
                    <DateRangePicker
                      handleDateChange={handleDateFilter}
                      disableFuture
                    />
                  </div>
                  <Autocomplete
                    value={selectedNames}
                    defaultValue={selectedNames}
                    onChange={(event, newValue) => {
                      setSelectedNames(newValue);
                    }}
                    options={coacheeNames}
                    getOptionLabel={option => option.coachee_name}
                    style={{ width: 240 }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder="Names"
                        variant="outlined"
                      />
                    )}
                  />
                  <Autocomplete
                    value={selectedSubject}
                    defaultValue={selectedSubject}
                    onChange={(event, newValue) => {
                      setSelectedSubject(newValue);
                    }}
                    options={subjectList}
                    groupBy={option => option.subject}
                    getOptionLabel={option => option.sub_subject}
                    style={{ width: 230 }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder="Subjects"
                        variant="outlined"
                      />
                    )}
                  />

                  <Autocomplete
                    value={selectedCampaign}
                    defaultValue={selectedCampaign}
                    onChange={(event, newValue) => {
                      setSelectedCampaign(newValue);
                    }}
                    options={campaignList}
                    getOptionLabel={option => `${option.functional_unit}`}
                    style={{ width: 200 }}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder="Campaign"
                        variant="outlined"
                      />
                    )}
                  />
                  <div>
                    <Button
                      onClick={() => {
                        setShowFilter(false);
                      }}
                    >
                      Clear all
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <WarningLettersTable
              getDataFrom={"/api/getWarningLetters"}
              uid={uid}
              searchKey={searchKey}
              filters={filterParam}
            />
          </Section>
        </PageContent>
      </div>
    </DashboardLayout>
  );
}
